import { FormControlLabel, FormGroup, Switch } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import UiButton from "components/base/UiButton";
import useGetOrganization from "hooks/organization/useGetOrganization";
import useUpdateOrganizationProposalDefaults from "hooks/organization/useUpdateOrganizationProposalDefaults";
import {
  ProposalFormsContext,
  ProposalFormsContextValue,
} from "modules/proposals/providers/ProposalFormsContextProvider";
import { useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import secondsLeft from "utils/dates/secondsLeft";

const PendingChangesContent = () => {
  const { productsFormsStatus, productsSubmitFunctions, autoSave, proposalDefaultsId } =
    useContext(ProposalFormsContext);
  const productsWithPendingChanges = Object.keys(productsFormsStatus).filter(
    (productId) => productsFormsStatus[productId].isDirty
  );

  const [showSaved, setShowSaved] = useState(false);
  useEffect(() => {
    const saved =
      !Object.values(productsFormsStatus).some(
        (form) => form.isDirty || form.status === "pending"
      ) && Object.values(productsFormsStatus).some((form) => form.status === "fulfilled");
    setShowSaved(saved);
    if (!saved) return;
    const timer = setTimeout(() => setShowSaved(false), 3000);
    return () => clearTimeout(timer);
  }, [productsFormsStatus]);

  return (
    <>
      {showSaved && (
        <MDBox
          role="status"
          aria-live="polite"
          sx={{
            width: "100%",
            display: "flex",
            background: "#fcfcfc",
            height: 42,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            alignItems: "center",
            justifyContent: "center",
            boxShadow: 3,
            zIndex: 200000,
          }}
        >
          <MDTypography sx={{ fontSize: "12px" }}>Saved</MDTypography>
        </MDBox>
      )}
      {productsWithPendingChanges.map((productId, i) => {
        const status = productsFormsStatus[productId].status;
        const isDirty = productsFormsStatus[productId].isDirty;
        const isRejected = status === "rejected";
        const isSaving = status === "pending";
        const isPending = (isDirty && !isSaving) || isRejected;

        return isPending || isSaving ? (
          <MDBox
            key={productId}
            sx={{
              width: "100%",
              display: "flex",
              background: "#fcfcfc",
              height: `${40 + productsWithPendingChanges.length * 2 - i * 2}px`,
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: 3,
              zIndex: 200000 + productsWithPendingChanges.length - i,
              gap: "8px",
              px: "12px",
            }}
          >
            {isSaving ? (
              <MDTypography role="status" sx={{ fontSize: "12px" }}>
                Saving…
              </MDTypography>
            ) : isRejected ? (
              <>
                <MDTypography sx={{ fontSize: "12px" }}>
                  Unable to save. Review the product and try again.
                </MDTypography>
                <UiButton
                  uiVariant="primary-green"
                  onClick={() => {
                    productsSubmitFunctions[productId]?.();
                  }}
                >
                  Save
                </UiButton>
              </>
            ) : (
              <PendingChangesCountdown
                attemptedToSaveAt={productsFormsStatus[productId].attemptToSaveAt}
                productsSubmitFunctions={productsSubmitFunctions}
                productId={productId}
                autoSave={autoSave}
                proposalDefaultsId={proposalDefaultsId}
              />
            )}
          </MDBox>
        ) : null;
      })}
    </>
  );
};

function PendingChangesCountdown({
  attemptedToSaveAt,
  productsSubmitFunctions,
  productId,
  autoSave,
  proposalDefaultsId,
}: {
  attemptedToSaveAt: number;
  productsSubmitFunctions: ProposalFormsContextValue["productsSubmitFunctions"];
  productId: string;
  autoSave: boolean;
  proposalDefaultsId: string;
}) {
  const [remainingSeconds, setRemainingSeconds] = useState(secondsLeft(attemptedToSaveAt));
  const interval = useRef<NodeJS.Timer | null>(null);
  const [updateOrganizationProposalDefaults] = useUpdateOrganizationProposalDefaults();

  useEffect(() => {
    if (autoSave) {
      interval.current = setInterval(() => {
        setRemainingSeconds(secondsLeft(attemptedToSaveAt));
      }, 1000);
      // @ts-ignore
      return () => interval.current && clearInterval(interval.current);
    }
  }, [attemptedToSaveAt, autoSave]);

  return (
    <>
      <MDTypography sx={{ fontSize: "12px" }}>
        {autoSave
          ? `Pending Changes saving in ${remainingSeconds > 0 ? remainingSeconds : 0} seconds... `
          : "You have unsaved changes"}
      </MDTypography>
      <UiButton
        uiVariant="primary-green"
        onClick={() => {
          productsSubmitFunctions[productId]?.();
        }}
      >
        Save
      </UiButton>

      <MDBox ml={2}>
        <FormGroup>
          <FormControlLabel
            sx={{ "& .MuiFormControlLabel-label": { fontSize: "12px" } }}
            control={
              <Switch
                size="small"
                checked={autoSave}
                onChange={(e) => {
                  productsSubmitFunctions[productId]?.();
                  updateOrganizationProposalDefaults({
                    variables: {
                      input: {
                        id: proposalDefaultsId,
                        autoSave: e.target.checked,
                      },
                    },
                  });
                }}
              />
            }
            label="Auto-Save"
          />
        </FormGroup>
      </MDBox>
    </>
  );
}

export default function PendingChanges() {
  const pendingChangesRoot = document.getElementById("pending-changes");

  return ReactDOM.createPortal(<PendingChangesContent />, pendingChangesRoot);
}
